/**
 * Shared constants and utilities for the Task Detail Modal system.
 * Extracted from BasicInfoTab and TaskModalFooter for cross-tab reuse.
 */

import { chassisApi } from '../../../services/chassisApi'
import type { Chassis } from '../../../types/chassis'

// ── Status Phase definitions (per STATUS_DESIGN.md §2.2) ──
export const STATUS_PHASES = [
  {
    name: 'Waiting',
    statuses: ['on-vessel', 'released', 'on-hold', 'yard-stored', 'pcc-hold'],
    activeColor: 'bg-purple-500',
    activeBg: 'bg-purple-50',
    activeBorder: 'border-purple-200',
    activeText: 'text-purple-700',
    dotColor: 'bg-purple-400',
  },
  {
    name: 'Dispatch',
    statuses: ['unassigned', 'assigned'],
    activeColor: 'bg-blue-500',
    activeBg: 'bg-blue-50',
    activeBorder: 'border-blue-200',
    activeText: 'text-blue-700',
    dotColor: 'bg-blue-400',
  },
  {
    name: 'Execution',
    statuses: ['picking', 'delivering', 'on-stop', 'loading', 'empty', 'loaded', 'returning'],
    activeColor: 'bg-indigo-500',
    activeBg: 'bg-indigo-50',
    activeBorder: 'border-indigo-200',
    activeText: 'text-indigo-700',
    dotColor: 'bg-indigo-400',
  },
  {
    name: 'Final',
    statuses: ['completed', 'uncompleted', 'cancelled', 'paused'],
    activeColor: 'bg-emerald-500',
    activeBg: 'bg-emerald-50',
    activeBorder: 'border-emerald-200',
    activeText: 'text-emerald-700',
    dotColor: 'bg-emerald-400',
  },
] as const

export function getPhaseIndex(status: string): number {
  return STATUS_PHASES.findIndex(p => (p.statuses as readonly string[]).includes(status))
}

// Statuses that can be paused (Execution phase)
export const PAUSABLE_STATUSES = ['picking', 'delivering', 'on-stop', 'loading', 'empty', 'loaded', 'returning']

/**
 * Valid state transitions — mirrors backend STATE_TRANSITIONS in taskStateMachine.js.
 * Each key maps to an array of valid next statuses.
 */
export const STATE_TRANSITIONS: Record<string, string[]> = {
  'on-vessel':   ['released', 'on-hold'],
  'released':    ['unassigned', 'on-hold'],
  // 'on-vessel' is the correction path — a hold reported on a container that
  // was never discharged means the ship simply has not arrived yet.
  'on-hold':     ['released', 'unassigned', 'on-vessel'],
  'unassigned':  ['assigned'],
  'assigned':    ['picking', 'unassigned'],
  'picking':     ['delivering', 'returning', 'yard-stored'],
  'delivering':  ['on-stop', 'completed', 'loading', 'returning', 'yard-stored'],
  'on-stop':     ['delivering'],
  'loading':     ['empty', 'loaded'],
  'empty':       ['returning', 'unassigned'],
  'loaded':      ['returning', 'unassigned'],
  'returning':   ['completed'],
  'yard-stored': ['unassigned'],
  'pcc-hold':    ['unassigned'],
  'paused':      [],            // handled by resume logic
  'completed':   [],
  'uncompleted': [],
  'cancelled':   [],
}

/**
 * Primary "advance" status for each status — the most common happy-path next step.
 * Returns null for terminal statuses or statuses where there is no single obvious next.
 */
const ADVANCE_STATUS: Record<string, string | null> = {
  'on-vessel':   'released',
  'released':    'unassigned',
  'on-hold':     'released',
  'unassigned':  'assigned',
  'assigned':    'picking',
  'picking':     'delivering',
  'delivering':  null,           // depends on subtype → loading (liveload) or completed (drop)
  'on-stop':     'delivering',
  'loading':     null,           // depends on import/export → empty or loaded
  'empty':       'returning',
  'loaded':      'returning',
  'returning':   'completed',
  'yard-stored': 'unassigned',
  'pcc-hold':    'unassigned',
  'paused':      null,
  'completed':   null,
  'uncompleted': null,
  'cancelled':   null,
}

/**
 * Statuses each task type can hold — mirrors TASK_TYPE_STATUSES in the backend's
 * taskStateMachine.js, which rejects anything outside the set.
 *
 * STATE_TRANSITIONS above only knows "which status may follow which", so it lets
 * a Drop load-out go delivering → loading → empty. Those belong to the follow-up
 * empty-in task: on a Drop order the driver leaves the box and the task ends at
 * completed. Offering them here just produces a rejected request and a task that
 * can never complete (so its Task 2 is never created).
 */
const UNIVERSAL_TASK_STATUSES = [
  'unassigned', 'assigned', 'picking', 'delivering', 'on-stop',
  'yard-stored', 'pcc-hold', 'paused', 'completed', 'cancelled', 'uncompleted',
]

// Warehouse-dock statuses — a Liveload driver waits through them on the
// outbound task; a Drop driver does not.
const DOCK_STATUSES = ['loading', 'empty', 'loaded']

const TASK_TYPE_STATUSES: Record<string, { base: string[]; liveload: string[] }> = {
  'load-out':  { base: ['on-vessel', 'released', 'on-hold'], liveload: [...DOCK_STATUSES, 'returning'] },
  'empty-out': { base: ['released', 'on-hold'],              liveload: [...DOCK_STATUSES, 'returning'] },
  'delivery':  { base: [],                                   liveload: [...DOCK_STATUSES, 'returning'] },
  'empty-in':  { base: ['loading', 'empty', 'returning'],    liveload: [] },
  'load-in':   { base: ['loading', 'loaded', 'returning'],   liveload: [] },
  'return':    { base: ['loading', 'empty', 'loaded'],       liveload: [] },
}

/**
 * Statuses this task is allowed to hold. Returns null for an unknown task type
 * (no opinion — don't hide anything).
 */
export function getAllowedStatuses(
  taskType?: string,
  taskSubtype?: string,
  importExport?: string
): Set<string> | null {
  const type = (taskType || '').toLowerCase().replace(/[_\s]/g, '-')
  const config = TASK_TYPE_STATUSES[type]
  if (!config) return null

  const allowed = new Set([
    ...UNIVERSAL_TASK_STATUSES,
    ...config.base,
    ...(taskSubtype === 'live-load' ? config.liveload : []),
  ])

  // An import box is never 'loaded'; an export box is never 'empty'.
  const ie = (importExport || '').toLowerCase()
  if (ie === 'import') allowed.delete('loaded')
  else if (ie === 'export') allowed.delete('empty')

  return allowed
}

/**
 * Get the primary advance status for a given status.
 * For 'loading', uses import/export to determine empty vs loaded.
 */
export function getAdvanceStatus(status: string, importExport?: string, taskSubtype?: string): string | null {
  // Liveload: delivering → loading (driver waits at warehouse for unload/load)
  // Drop&Pick: delivering → completed (drop container and leave)
  if (status === 'delivering') {
    return taskSubtype === 'live-load' ? 'loading' : 'completed'
  }
  if (status === 'loading') {
    return importExport === 'export' ? 'loaded' : 'empty'
  }
  return ADVANCE_STATUS[status] ?? null
}

// Module-level cache for dropdown data to prevent loading flicker on tab switches
interface DropdownDataCache {
  chassis: Chassis[] | null
  chassisPromise: Promise<Chassis[]> | null
}

export const dropdownCache: DropdownDataCache = {
  chassis: null,
  chassisPromise: null,
}

/**
 * Pre-warm the dropdown cache to prevent loading flicker on first modal open
 */
export const preloadDropdownData = async (): Promise<void> => {
  if (!dropdownCache.chassis && !dropdownCache.chassisPromise) {
    dropdownCache.chassisPromise = (async () => {
      try {
        const response = await chassisApi.getAll({ limit: 100 })
        const data = response.data || []
        dropdownCache.chassis = data
        return data
      } catch (error) {
        console.error('Failed to preload chassis:', error)
        return []
      }
    })()
    await dropdownCache.chassisPromise
  }
}
